import { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react'
import { apiFetch } from '@/lib/api'

const FlagContext = createContext(null)

export function FlagProvider({ children }) {
  const [menu, setMenu] = useState(null) // { x, y, word, source }
  const [flaggedWords, setFlaggedWords] = useState(() => new Set())

  const openMenu = useCallback((x, y, word, source) => {
    setMenu({ x, y, word: word.toLowerCase().trim(), source })
  }, [])

  const closeMenu = useCallback(() => setMenu(null), [])

  const flagWord = useCallback((word, source) => {
    const clean = word.toLowerCase().trim()
    if (!clean) return
    apiFetch('/api/flag', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ word: clean, source }),
    })
      .then(res => res.ok ? res.json() : null)
      .then(() => setFlaggedWords(prev => new Set(prev).add(clean)))
      .catch(() => {})
  }, [])

  const isFlagged = useCallback((word) => {
    return flaggedWords.has(word.toLowerCase().trim())
  }, [flaggedWords])

  return (
    <FlagContext.Provider value={{ openMenu, closeMenu, flagWord, isFlagged, menu }}>
      {children}
      {menu && (
        <FlagContextMenu
          x={menu.x}
          y={menu.y}
          word={menu.word}
          source={menu.source}
          onFlag={() => { flagWord(menu.word, menu.source); closeMenu() }}
          onClose={closeMenu}
        />
      )}
    </FlagContext.Provider>
  )
}

export function useFlag() {
  return useContext(FlagContext)
}

function FlagContextMenu({ x, y, word, onFlag, onClose }) {
  const ref = useRef(null)

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    function handleKey(e) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [onClose])

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        top: y,
        left: x,
        zIndex: 1000,
        background: '#2a2018',
        border: '1px solid #5a4a38',
        borderRadius: '8px',
        padding: '4px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
        minWidth: '160px',
      }}
    >
      <button
        type="button"
        onClick={onFlag}
        style={{
          display: 'block',
          width: '100%',
          padding: '8px 12px',
          fontSize: '13px',
          fontWeight: '600',
          color: '#e8ddd0',
          background: 'none',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'background-color 0.1s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212, 168, 68, 0.15)'; e.currentTarget.style.color = '#d4a844' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#e8ddd0' }}
      >
        Flag &ldquo;{word}&rdquo; for review
      </button>
    </div>
  )
}
